import {
  FluidContextMenuSection,
  FluidContextMenuToggle,
  FluidFilterSet,
  FluidFilterType,
  FluidRowData,
  FluidTableHeader,
  getAtPath,
  normalise,
} from '@lmig/fluid-core';

import {
  BLANK_FILTER_ID_VALUE,
  BLANK_FILTER_LABEL,
  FILTER_TOGGLE_ID,
} from './fluid-filter.constants';
import { findCustomFilter } from './fluid-table-filter.utils';

/**
 * Creates a custom filter whose predicate is handled by the custom filter passed in via the header.
 * @param filterToggle
 * @param header
 */
export const CUSTOM_FILTER = (
  filterToggle: FluidContextMenuToggle,
  header: FluidTableHeader
) => ({
  label: filterToggle.label,
  predicate: findCustomFilter(header, filterToggle.label).predicate,
  id:
    filterToggle.id ||
    FILTER_TOGGLE_ID(header.dataPath, normalise(filterToggle.label)),
});

/**
 * Creates a column filter whose predicate checks for the existence of the given label at the dataPath.
 * @param filterToggle
 * @param header
 */
export const COLUMN_FILTER = (
  filterToggle: FluidContextMenuToggle,
  header: FluidTableHeader
) => ({
  label: filterToggle.label,
  predicate: (dataItem: FluidRowData) => {
    const dataItemAtPath = getAtPath(dataItem, header.dataPath);

    if (Array.isArray(dataItemAtPath)) {
      return dataItemAtPath.some(
        (elementOfDataItemAtPath) =>
          (header.columnFilterCompareFormat
            ? normalise(
                header.columnFilterCompareFormat(elementOfDataItemAtPath)
              )
            : normalise(elementOfDataItemAtPath)) ===
          filterToggle.normalisedValue
      );
    } else {
      return (
        filterToggle.normalisedValue ===
        (header.columnFilterCompareFormat
          ? normalise(header.columnFilterCompareFormat(dataItemAtPath))
          : normalise(dataItemAtPath))
      );
    }
  },
  id:
    filterToggle.id ||
    FILTER_TOGGLE_ID(header.dataPath, normalise(filterToggle.label)),
});

/**
 * Creates a filter whose predicate checks for the existence of a value.
 * @param filterToggle
 * @param header
 */
export const BLANK_FILTER = (
  filterToggle: FluidContextMenuToggle,
  header: FluidTableHeader
) => ({
  label: filterToggle.label,
  predicate: (dataItem: FluidRowData) => !getAtPath(dataItem, header.dataPath),
  id: FILTER_TOGGLE_ID(header.dataPath, BLANK_FILTER_ID_VALUE),
});

/**
 * Creates a single filter set of the type of filter, whether it is applied or not, and filters within it
 * that are actioned.
 * @param filterType
 * @param selected
 * @param menuSection
 * @param header
 */
export const FILTER_SET = (
  filterType: FluidFilterType,
  selected: boolean,
  menuSection: FluidContextMenuSection,
  header: FluidTableHeader
): FluidFilterSet => {
  return {
    type: menuSection.title,
    filterType,
    dataPath: header.dataPath,
    filters: menuSection.toggles
      .filter((toggle) => toggle.selected === selected)
      .map((toggle) => FILTER(toggle, header, filterType)),
  };
};

/**
 * Creates a single filter based on filterType and toggle state.
 * @param toggle
 * @param header
 * @param filterType
 */
export const FILTER = (
  toggle: FluidContextMenuToggle,
  header: FluidTableHeader,
  filterType: FluidFilterType
) => {
  return filterType === 'custom'
    ? CUSTOM_FILTER(toggle, header)
    : toggle.label === BLANK_FILTER_LABEL
    ? BLANK_FILTER(toggle, header)
    : COLUMN_FILTER(toggle, header);
};
